import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { apiErrorMessage } from '../../../utils/errors';
import {
  useListAddresses,
  useCreateAddress,
  useUpdateAddress,
  useDeleteAddress,
} from '../../../features/addresses/api';
import { useCurrentUser } from '../../../features/auth/api';
import '../settings.scss';
import AddressAutocomplete from '../../../components/AddressAutocomplete/AddressAutocomplete';
import OperatingHoursEditor from '../../../components/OperatingHoursEditor/OperatingHoursEditor';
import {
  coerceOperatingHours,
  type OperatingHours,
} from '../../../features/addresses/operatingHours';

export default function AddressesTab() {
  const { data: addresses = [], isLoading, isFetching } = useListAddresses();

  const [openDrawer, setOpenDrawer] = useState(false);
  const [editing, setEditing] = useState<any>(null);

  const onAdd = () => {
    setEditing(null);
    setOpenDrawer(true);
  };

  const onEdit = (addr: any) => {
    setEditing(addr);
    setOpenDrawer(true);
  };

  const { data: apiUser } = useCurrentUser();
  const apiUserObj = apiUser?.user ?? apiUser ?? null;
  const userType = apiUserObj?.type;

  return (
    <div className="card">
      <div className="flex-between" style={{ marginBottom: 12 }}>
        <div>
          <h2 className="card__title">Delivery Addresses</h2>
        </div>
        {userType === 'USER' && (
          <button className="btn btn--primary" onClick={onAdd}>
            Add address
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="skeleton" style={{ height: 220 }} />
      ) : addresses.length === 0 ? (
        <div className="card card--bleed-top" style={{ padding: 16 }}>
          <div className="muted">No addresses yet. Add your first one.</div>
        </div>
      ) : (
        <div className="address-grid card card--bleed-top">
          {addresses.map((a: any) => (
            <AddressCard key={a.id} a={a} onEdit={() => onEdit(a)} />
          ))}
        </div>
      )}

      {isFetching && (
        <div className="muted" style={{ marginTop: 8 }}>
          Refreshing…
        </div>
      )}

      {openDrawer && (
        <AddressDrawer
          key={editing?.id || 'new'} // <-- force a fresh mount per record
          initial={editing}
          onClose={() => setOpenDrawer(false)}
        />
      )}
    </div>
  );
}

function AddressCard({ a, onEdit }: { a: any; onEdit: () => void }) {
  const del = useDeleteAddress();

  const handleDelete = async () => {
    const ok = window.confirm(
      "Delete this address? Existing orders will keep their saved address, but new orders won't be able to use it."
    );
    if (!ok) return;
    try {
      await del.mutateAsync(a.id);
      toast.success('Address deleted.');
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to delete address.'));
    }
  };

  const { data: apiUser } = useCurrentUser();
  const apiUserObj = apiUser?.user ?? apiUser ?? null;
  const userType = apiUserObj?.type;

  return (
    <div className="address-card">
      <div className="address-card__head">
        <div className="address-card__title-wrap">
          <strong>{a.label || 'Address'}</strong>

          <div className="address-card__badges">
            {a.isPrimary && <span className="badge">Primary</span>}
            {a.isHeadquarters && <span className="badge">Headquarters</span>}
          </div>
        </div>

        {userType === 'USER' && (
          <div className="actions address-card__actions">
            <button className="btn btn--ghost" onClick={onEdit}>
              Edit
            </button>
            <button className="btn btn--danger" onClick={handleDelete}>
              Delete
            </button>
          </div>
        )}
      </div>

      <div className="address-card__body">
        <div>
          <strong>{a.fullName}</strong>
          {a.phone ? ` • ${formatPhone(a.phone)}` : ''}
        </div>
        <div>{a.line1}</div>
        {a.line2 && <div>{a.line2}</div>}
        <div>
          {a.city}
          {a.state ? `, ${a.state}` : ''} {a.postalCode}
        </div>
        <div>{a.country}</div>
      </div>
    </div>
  );
}

function formatPhone(us: unknown) {
  // Exported from utils/formatters — kept here for onBlur input masking
  const digits = String(us || '')
    .replace(/\D/g, '')
    .slice(0, 10);
  if (!digits) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function AddressDrawer({ initial, onClose }: { initial: any; onClose: () => void }) {
  const isEdit = !!initial;

  const create = useCreateAddress();
  const update = useUpdateAddress();

  const [form, setForm] = useState({
    label: '',
    fullName: '',
    phone: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'USA',
    isPrimary: false,
    isHeadquarters: false,
    operatingHours: null as OperatingHours | null,
    deliveryNotes: '',
  });

  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (initial) {
      setForm({
        label: initial.label || '',
        fullName: initial.fullName || '',
        phone: initial.phone || '',
        line1: initial.line1 || '',
        line2: initial.line2 || '',
        city: initial.city || '',
        state: initial.state || '',
        postalCode: initial.postalCode || '',
        country: initial.country || 'USA',
        isPrimary: !!initial.isPrimary,
        isHeadquarters: !!initial.isHeadquarters,
        operatingHours: coerceOperatingHours(initial.operatingHours),
        deliveryNotes: initial.deliveryNotes || '',
      });
    }
  }, [initial]);

  const requiredMissing = useMemo(() => {
    const req = ['fullName', 'phone', 'line1', 'city', 'state', 'postalCode', 'country'];
    return req.filter((k) => !String((form as any)[k] || '').trim());
  }, [form]);

  const disabled = requiredMissing.length > 0;

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const onPhoneBlur = () => {
    setForm((f) => ({ ...f, phone: formatPhone(f.phone) }));
  };

  const onSubmit = async () => {
    try {
      setShowErrors(true);
      if (disabled) return;

      const payload = {
        ...form,
        isPrimary: !!form.isPrimary,
        isHeadquarters: !!form.isHeadquarters,
      };

      if (isEdit) {
        await update.mutateAsync({ id: initial.id, ...payload });
        toast.success('Address updated.');
      } else {
        await create.mutateAsync(payload);
        toast.success('Address added.');
      }
      onClose();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Save failed.'));
    }
  };

  const isErr = (key: string) => showErrors && requiredMissing.includes(key);

  return (
    <div className="drawer">
      <div className="drawer__panel address-drawer">
        {/* Header */}
        <div className="drawer__header">
          <h3 style={{ margin: 0 }}>{isEdit ? 'Edit address' : 'Add address'}</h3>
          <button className="btn btn--ghost" onClick={onClose}>
            Close
          </button>
        </div>

        {/* Content */}
        <div className="drawer__content">
          <div className="section">
            <div className="grid grid--2">
              <div className={`form-control ${isErr('label') ? 'has-error' : ''}`}>
                <label>Company Location Name</label>
                <input
                  name="label"
                  value={form.label}
                  onChange={onChange}
                  placeholder="HQ, Site A, Garage, etc."
                />
              </div>

              <div className={`form-control ${isErr('fullName') ? 'has-error' : ''}`}>
                <label>Site Contact</label>
                <input name="fullName" value={form.fullName} onChange={onChange} />
                {isErr('fullName') && <div className="error-text">Required</div>}
              </div>

              <div className={`form-control wide ${isErr('phone') ? 'has-error' : ''}`}>
                <label>Site Phone Number</label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  onBlur={onPhoneBlur}
                  placeholder="(555) 555-5555"
                />
                {isErr('phone') && <div className="error-text">Required</div>}
              </div>
            </div>
          </div>

          {/* Location section */}
          <div className="section">
            <div className="section__title">Location</div>

            <div className="grid grid--2">
              <div className={`form-control ${isErr('line1') ? 'has-error' : ''}`}>
                <label>Address line 1</label>

                <AddressAutocomplete
                  value={form.line1}
                  onChange={(e) => setForm((f) => ({ ...f, line1: e.target.value }))}
                  onPlaceSelected={(fields) =>
                    setForm((f) => ({
                      ...f,
                      line1: fields.line1,
                      city: fields.city,
                      state: fields.state,
                      postalCode: fields.postalCode,
                      country: fields.country,
                      // Google returns the unit as subpremise; fill it when present.
                      // A fresh pick is authoritative — set the unit or clear a stale one.
                      line2: fields.line2,
                    }))
                  }
                />

                {isErr('line1') && <div className="error-text">Required</div>}
              </div>
            </div>

            <div className="field-row">
              <div className={`form-control ${isErr('city') ? 'has-error' : ''}`}>
                <label>City</label>
                <input name="city" value={form.city} onChange={onChange} />
                {isErr('city') && <div className="error-text">Required</div>}
              </div>

              <div className={`form-control short ${isErr('state') ? 'has-error' : ''}`}>
                <label>State / Region</label>
                <input name="state" value={form.state} onChange={onChange} />
                {isErr('state') && <div className="error-text">Required</div>}
              </div>

              <div className={`form-control short ${isErr('postalCode') ? 'has-error' : ''}`}>
                <label>Postal code</label>
                <input name="postalCode" value={form.postalCode} onChange={onChange} />
                {isErr('postalCode') && <div className="error-text">Required</div>}
              </div>
            </div>

            <div className={`form-control medium ${isErr('country') ? 'has-error' : ''}`}>
              <label>Country</label>
              <input name="country" value={form.country} onChange={onChange} />
              {isErr('country') && <div className="error-text">Required</div>}
            </div>
          </div>

          {/* Primary toggle */}
          <div className="section">
            <label className="switch">
              <input
                type="checkbox"
                name="isPrimary"
                checked={form.isPrimary}
                onChange={(e) => setForm((f) => ({ ...f, isPrimary: e.target.checked }))}
              />
              <span className="switch__track" />
              <span className="switch__label">Set as primary</span>
            </label>
            <div className="muted" style={{ marginTop: 4 }}>
              If checked, this becomes your default delivery address.
            </div>

            <label className="switch" style={{ marginTop: 12 }}>
              <input
                type="checkbox"
                name="isHeadquarters"
                checked={form.isHeadquarters}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    isHeadquarters: e.target.checked,
                  }))
                }
              />
              <span className="switch__track" />
              <span className="switch__label">Set as headquarters</span>
            </label>

            <div className="muted" style={{ marginTop: 4 }}>
              If checked, this address will be marked as the customer headquarters.
            </div>

            <OperatingHoursEditor
              value={form.operatingHours}
              onChange={(operatingHours) => setForm((f) => ({ ...f, operatingHours }))}
            />

            <div className="form-control" style={{ marginTop: 8 }}>
              <label>Delivery notes</label>
              <textarea
                rows={3}
                value={form.deliveryNotes}
                onChange={(e) => setForm((f) => ({ ...f, deliveryNotes: e.target.value }))}
                placeholder="Gate hours, truck access, where to dump…"
              />
            </div>
          </div>
        </div>

        {/* Sticky footer */}
        <div className="drawer__footer">
          <div className="actions" style={{ justifyContent: 'flex-end' }}>
            <button
              className="btn btn--primary"
              onClick={onSubmit}
              disabled={disabled || create.isPending || update.isPending}
            >
              {create.isPending || update.isPending
                ? 'Saving…'
                : isEdit
                  ? 'Save changes'
                  : 'Add address'}
            </button>
          </div>
        </div>
      </div>

      <div className="drawer__backdrop" onClick={onClose} />
    </div>
  );
}
